import React, {useState} from 'react';
import {IconButton} from '@mui/material';
import {Icon} from '@lightricks/react-design-system';
import type {IconName} from '@lightricks/react-design-system';
import AnalyticsService, {
  eventNames,
} from '@/services/analytics/AnalyticsService';
import translate from '@/utils/translate';
import CAMPAIGN_ACTION, {actionsForState} from '@/config/campaignAction';
import type {CampaignActionId} from '@/config/campaignAction';
import ConfirmModal from '@/components/confirm-modal';
import Dropdown from '@/components/dropdown';
import type {DropdownOption} from '@/components/dropdown';
import useCloneCampaign from '@/hooks/mutations/use-clone-campaign';
import useCloseCampaign from '@/hooks/mutations/use-close-campaign';
import useDeleteCampaign from '@/hooks/mutations/use-delete-campaign';
import useRevertCampaignToDraft from '@/hooks/mutations/use-revert-campaign-to-draft';
import useSubmitCampaignCloseFeedback from '@/hooks/mutations/use-submit-campaign-close-feedback';
import useToggleCampaignApplications from '@/hooks/mutations/use-toggle-campaign-applications';
import useNavigation from '@/hooks/use-navigation';
import {getCampaignBriefEditUrl} from '../../utils/getCampaignCardUrl';
import CampaignCloseModal from './CampaignCloseModal';
import CloneNetworkSelector from './CloneNetworkSelector';

interface MenuItemSpec {
  id: CampaignActionId;
  iconName: IconName;
  labelKey: string;
}

const ITEM_SPECS: Record<CampaignActionId, MenuItemSpec> = {
  [CAMPAIGN_ACTION.DELETE_DRAFT]: {
    id: CAMPAIGN_ACTION.DELETE_DRAFT,
    iconName: 'Actions-Remove',
    labelKey: 'Delete draft',
  },
  [CAMPAIGN_ACTION.CLONE_BRIEF]: {
    id: CAMPAIGN_ACTION.CLONE_BRIEF,
    iconName: 'Actions-Duplicate',
    labelKey: 'Clone brief',
  },
  [CAMPAIGN_ACTION.COMPLETE_BRIEF]: {
    id: CAMPAIGN_ACTION.COMPLETE_BRIEF,
    iconName: 'Navigation-Edit',
    labelKey: 'Complete brief',
  },
  [CAMPAIGN_ACTION.REVERT_TO_DRAFT]: {
    id: CAMPAIGN_ACTION.REVERT_TO_DRAFT,
    iconName: 'Navigation-Edit',
    labelKey: 'Revert to draft',
  },
  [CAMPAIGN_ACTION.PAUSE_APPLICATIONS]: {
    id: CAMPAIGN_ACTION.PAUSE_APPLICATIONS,
    iconName: 'Actions-Pause',
    labelKey: 'Pause new applications',
  },
  [CAMPAIGN_ACTION.RESUME_APPLICATIONS]: {
    id: CAMPAIGN_ACTION.RESUME_APPLICATIONS,
    iconName: 'Actions-Play',
    labelKey: 'Resume new applications',
  },
  [CAMPAIGN_ACTION.CLOSE_CAMPAIGN]: {
    id: CAMPAIGN_ACTION.CLOSE_CAMPAIGN,
    iconName: 'Navigation-Flag',
    labelKey: 'Close campaign',
  },
};

const CONFIRM_MODAL_SETTINGS: Partial<
  Record<CampaignActionId, {title: string; body: string; confirm: string}>
> = {
  [CAMPAIGN_ACTION.DELETE_DRAFT]: {
    title: 'Delete draft?',
    body: 'This will permanently delete the draft. This action cannot be undone.',
    confirm: 'Delete',
  },
  [CAMPAIGN_ACTION.CLONE_BRIEF]: {
    title: 'Clone brief?',
    body: 'A copy of this brief will be added to your campaigns.',
    confirm: 'Clone',
  },
  [CAMPAIGN_ACTION.REVERT_TO_DRAFT]: {
    title: 'Revert to draft?',
    body: 'This will revert the campaign back to draft status.',
    confirm: 'Revert',
  },
};

const ANALYTICS_BUTTON_NAME: Record<CampaignActionId, string> = {
  [CAMPAIGN_ACTION.DELETE_DRAFT]: 'open_delete_draft_modal',
  [CAMPAIGN_ACTION.CLONE_BRIEF]: 'open_clone_brief_modal',
  [CAMPAIGN_ACTION.REVERT_TO_DRAFT]: 'open_revert_to_draft_modal',
  [CAMPAIGN_ACTION.COMPLETE_BRIEF]: 'complete_brief',
  [CAMPAIGN_ACTION.PAUSE_APPLICATIONS]: 'pause_new_applications',
  [CAMPAIGN_ACTION.RESUME_APPLICATIONS]: 'resume_new_applications',
  [CAMPAIGN_ACTION.CLOSE_CAMPAIGN]: 'open_close_campaign_modal',
};

interface CampaignActionsMenuProps {
  campaignId: string;
  campaignState: string;
  generatedByBriefAi: boolean;
  campaignNetwork?: string | null;
  canCloneCampaign?: boolean;
  canRevertCampaign?: boolean;
  canDeleteCampaign?: boolean;
  canEditCampaign?: boolean;
  canPauseApplications?: boolean;
  canResumeApplications?: boolean;
  canCloseCampaign?: boolean;
  onCloseFeedbackSubmitted?: () => void;
}

export default function CampaignActionsMenu({
  campaignId,
  campaignState,
  generatedByBriefAi,
  campaignNetwork,
  canCloneCampaign = true,
  canRevertCampaign = true,
  canDeleteCampaign = true,
  canEditCampaign = true,
  canPauseApplications = true,
  canResumeApplications = true,
  canCloseCampaign = true,
  onCloseFeedbackSubmitted,
}: CampaignActionsMenuProps) {
  const {navigate} = useNavigation();
  const [pendingAction, setPendingAction] = useState<CampaignActionId | null>(
    null
  );
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  // Once the close succeeds we keep this true so a feedback-submit retry (e.g.
  // after a network error) does not attempt to close the campaign again.
  const [campaignClosed, setCampaignClosed] = useState(false);
  const [cloneNetwork, setCloneNetwork] = useState<string | null | undefined>(
    undefined
  );
  const [cloneSelectorOpen, setCloneSelectorOpen] = useState(false);

  const resetCloneState = () => {
    setPendingAction(null);
    setCloneNetwork(undefined);
    setCloneSelectorOpen(false);
  };

  const deleteMutation = useDeleteCampaign();
  const revertMutation = useRevertCampaignToDraft();
  const toggleApplicationsMutation = useToggleCampaignApplications();
  const cloneMutation = useCloneCampaign({
    onSuccess: (data) => {
      resetCloneState();
      const clonedId = data.data.id;
      const clonedAi = data.data.generatedByBriefAi ?? false;
      if (clonedId) {
        navigate(getCampaignBriefEditUrl(clonedId, clonedAi));
      }
    },
    onError: () => {
      resetCloneState();
    },
  });
  const closeMutation = useCloseCampaign();
  const submitFeedbackMutation = useSubmitCampaignCloseFeedback();

  const isClosing = closeMutation.isLoading || submitFeedbackMutation.isLoading;

  const resetCloseModal = () => {
    setCloseModalOpen(false);
    setCampaignClosed(false);
  };

  const handleConfirmClose = async ({
    rating,
    comment,
  }: {
    rating: number;
    comment: string | null;
  }) => {
    try {
      if (!campaignClosed) {
        await closeMutation.mutateAsync(campaignId);
        setCampaignClosed(true);
      }
      await submitFeedbackMutation.mutateAsync({campaignId, rating, comment});
      resetCloseModal();
      onCloseFeedbackSubmitted?.();
    } catch {
      // Errors surface via each mutation's flash message; keep the modal open
      // so the brand can retry (the guard above skips re-closing).
    }
  };

  const actionIds = actionsForState(campaignState, {
    generatedByBriefAi,
    network: campaignNetwork,
    canClone: canCloneCampaign,
    canRevert: canRevertCampaign,
    canDelete: canDeleteCampaign,
    canEdit: canEditCampaign,
    canPauseApplications,
    canResumeApplications,
    canClose: canCloseCampaign,
  });

  function handleAction(actionId: CampaignActionId) {
    AnalyticsService.dispatchEvent(eventNames.BUTTON_PRESSED, {
      button_name: ANALYTICS_BUTTON_NAME[actionId],
      screen_name: 'campaigns_list',
      campaign_id: campaignId,
    });

    if (actionId === CAMPAIGN_ACTION.COMPLETE_BRIEF) {
      navigate(getCampaignBriefEditUrl(campaignId, generatedByBriefAi));
      return;
    }
    if (
      actionId === CAMPAIGN_ACTION.PAUSE_APPLICATIONS ||
      actionId === CAMPAIGN_ACTION.RESUME_APPLICATIONS
    ) {
      const action =
        actionId === CAMPAIGN_ACTION.PAUSE_APPLICATIONS ? 'pause' : 'resume';
      toggleApplicationsMutation.mutate({campaignId, action});
      return;
    }
    if (actionId === CAMPAIGN_ACTION.CLOSE_CAMPAIGN) {
      setCloseModalOpen(true);
      return;
    }
    if (CONFIRM_MODAL_SETTINGS[actionId]) {
      setPendingAction(actionId);
    }
  }

  const options: DropdownOption[] = actionIds.map((actionId) => {
    const spec = ITEM_SPECS[actionId];
    return {
      id: spec.id,
      label: translate(spec.labelKey),
      startIcon: (
        <Icon name={spec.iconName} size="medium" appearance="neutral" />
      ),
    };
  });

  const confirmModalSettings = pendingAction
    ? CONFIRM_MODAL_SETTINGS[pendingAction]
    : null;

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <Dropdown
        label=""
        options={options}
        selectedIds={[]}
        onChange={(option) => handleAction(option.id as CampaignActionId)}
        menuWidth={240}
        menuPositionHorizontal="right"
        showItemDividers={false}
        shouldStopEventPropagation
        disableSelectedBackground
        renderTrigger={({onClick, isOpen}) => (
          <IconButton
            onClick={onClick}
            aria-label={translate('More options')}
            aria-haspopup="menu"
            aria-expanded={isOpen}
          >
            <Icon
              name="Actions-More-Vertical"
              size="medium"
              appearance="neutral"
            />
          </IconButton>
        )}
      />

      {confirmModalSettings && (
        <ConfirmModal
          open={!!pendingAction}
          title={confirmModalSettings.title}
          body={confirmModalSettings.body}
          confirmLabel={confirmModalSettings.confirm}
          confirmAppearance={
            pendingAction === CAMPAIGN_ACTION.DELETE_DRAFT
              ? 'danger'
              : 'neutral'
          }
          isLoading={
            (pendingAction === CAMPAIGN_ACTION.CLONE_BRIEF &&
              cloneMutation.isLoading) ||
            (pendingAction === CAMPAIGN_ACTION.REVERT_TO_DRAFT &&
              revertMutation.isLoading)
          }
          isCancelDisabled={
            (pendingAction === CAMPAIGN_ACTION.CLONE_BRIEF &&
              cloneMutation.isLoading) ||
            (pendingAction === CAMPAIGN_ACTION.REVERT_TO_DRAFT &&
              revertMutation.isLoading)
          }
          isConfirmDisabled={
            pendingAction === CAMPAIGN_ACTION.CLONE_BRIEF &&
            cloneSelectorOpen &&
            cloneNetwork === undefined
          }
          onConfirm={() => {
            if (pendingAction === CAMPAIGN_ACTION.DELETE_DRAFT) {
              deleteMutation.mutate(campaignId);
              setPendingAction(null);
            } else if (pendingAction === CAMPAIGN_ACTION.CLONE_BRIEF) {
              cloneMutation.mutate({
                campaignId,
                ...(cloneNetwork !== undefined && {network: cloneNetwork}),
              });
            } else if (pendingAction === CAMPAIGN_ACTION.REVERT_TO_DRAFT) {
              revertMutation.mutate(campaignId);
              setPendingAction(null);
            }
          }}
          onCancel={resetCloneState}
        >
          {pendingAction === CAMPAIGN_ACTION.CLONE_BRIEF && (
            <CloneNetworkSelector
              selectedNetwork={cloneNetwork}
              sourceNetwork={campaignNetwork}
              disabled={cloneMutation.isLoading}
              onChange={setCloneNetwork}
              onSelectorToggle={setCloneSelectorOpen}
            />
          )}
        </ConfirmModal>
      )}

      <CampaignCloseModal
        open={closeModalOpen}
        isSubmitting={isClosing}
        onCancel={resetCloseModal}
        onConfirm={handleConfirmClose}
      />
    </div>
  );
}
